/**
 * Platform console → one school, managed as support.
 *
 * `/admin/schools/:slug/*` renders the SCHOOL console's own pages — curriculum,
 * staff, classes, students, books, activity — for a school the super admin does not
 * belong to. Nothing here re-implements them: the API client sends this school's
 * slug on `/tenant-admin` calls made from under this path (see
 * `supportSchoolSlug`), and the server admits a super admin there as an audited
 * support action. So every rule a school admin is held to, support is held to,
 * because it is the same handler.
 *
 * The school can see it happened: opening this writes to its activity log.
 */
import { createContext, useContext, useState } from 'react';
import { NavLink, Outlet, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { Breadcrumbs, Card } from '@/components/ds';
import { QueryState } from '@/components/admin/QueryState';

interface SupportSchool {
  slug: string;
  name: string;
}

const SupportSchoolContext = createContext<SupportSchool | null>(null);

/** The school being managed as support, or null on every other screen. */
export function useSupportSchool(): SupportSchool | null {
  return useContext(SupportSchoolContext);
}

interface TenantSummary {
  id: string;
  name: string;
  slug: string;
  type: 'FIRST_PARTY' | 'SCHOOL';
  status: 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
}

// In the order a school is set up, because each step reads the one above it.
const SECTIONS = [
  { to: 'curriculum', label: 'Curriculum' },
  { to: 'staff', label: 'Staff' },
  { to: 'classrooms', label: 'Classes' },
  { to: 'students', label: 'Students' },
  { to: 'books', label: 'Books' },
  { to: 'audit', label: 'Activity log' },
] as const;

export function SupportSchoolLayout() {
  const { slug = '' } = useParams();
  // Keyed so that moving to another school mounts a fresh scope — see below.
  return <SupportSchoolScope key={slug} slug={slug} />;
}

function SupportSchoolScope({ slug }: { slug: string }) {
  // The school console's cache keys carry no workspace — inside a school's own
  // URL there is only ever one. Here there can be several in one tab, so the
  // cache is emptied as each school's scope mounts, BEFORE any page below reads
  // it. A lazy initialiser runs during render, which is why it is used: an
  // effect runs after the first page has already drawn the last school's roster
  // under this school's name.
  useState(() => queryClient.removeQueries({ queryKey: ['tenant-admin'] }));

  const tenants = useQuery({
    queryKey: ['platform', 'tenants'],
    queryFn: () => apiGet<TenantSummary[]>('/platform/tenants'),
  });
  const school = tenants.data?.find((row) => row.slug === slug && row.type === 'SCHOOL');

  return (
    <div className="flex w-full max-w-5xl flex-col gap-4">
      <Breadcrumbs
        items={[{ label: 'Schools', to: '/admin/schools' }, { label: school?.name ?? slug }]}
      />

      <QueryState
        isPending={tenants.isPending}
        error={tenants.error}
        isEmpty={!school}
        empty={<p className="text-sm text-ink-soft">No school at /school/{slug}.</p>}
      >
        {school && school.status !== 'ACTIVE' ? (
          // A suspended school does not resolve for anyone, support included —
          // that is what suspension means. Reactivate it to manage it.
          <Card padding="14px 16px">
            <p className="text-sm text-ink">
              <strong>{school.name}</strong> is{' '}
              {school.status === 'SUSPENDED' ? 'suspended' : 'archived'}. Reactivate it from the
              schools list to manage it.
            </p>
          </Card>
        ) : (
          school && (
            <SupportSchoolContext.Provider value={{ slug, name: school.name }}>
              <div className="flex flex-col gap-3">
                <p className="text-xs text-ink-soft">
                  You are managing this school as CWY support. Every change is made with your name
                  on it, and opening this console is recorded in the school’s activity log.
                </p>
                <nav className="flex flex-wrap gap-1.5" aria-label={`${school.name} sections`}>
                  {SECTIONS.map((section) => (
                    <NavLink
                      key={section.to}
                      to={section.to}
                      className="rounded-full px-3 py-1 text-fs-13 font-semibold no-underline transition-colors duration-150"
                      style={({ isActive }) => ({
                        background: isActive ? 'var(--surface-inverse)' : 'transparent',
                        color: isActive ? 'var(--text-on-forest)' : 'var(--text-body)',
                        border: '1px solid var(--border-default)',
                      })}
                    >
                      {section.label}
                    </NavLink>
                  ))}
                </nav>
              </div>
              <Outlet />
            </SupportSchoolContext.Provider>
          )
        )}
      </QueryState>
    </div>
  );
}
